import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Blurb } from 'components/Containers';
import AcknowledgeSafeReturnModal from 'components/modals/AcknowledgeSafeReturnModal';
import RequestSafeReturnModal from 'components/modals/RequestSafeReturnModal';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import { simpleDate } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import LocationEpisodeSummary from 'models/LocationEpisodeSummary';
import Profile, { PERMISSIONS } from 'models/Profile';
import SafeReturn from 'models/reviews/SafeReturn';
import theme from 'styles/theme/theme';
import { BodySmallBold, Label, LabelBold } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';

function Title({ review, isProviderUser }: { review?: SafeReturn; isProviderUser: boolean }) {
  const title = useMemo(() => {
    if (review?.acknowledged) return 'Safe Return Request Acknowledged';
    if (review?.active) return 'Safe Return to SNF Requested';
    if (isProviderUser) return 'Request a Safe Return to SNF';
    return 'Prior SNF for Safe Return';
  }, [review, isProviderUser]);

  return <BodySmallBold>{title}</BodySmallBold>;
}

function Body({
  review,
  isProviderUser,
  previousSNFStay,
}: {
  review?: SafeReturn;
  isProviderUser: boolean;
  previousSNFStay?: LocationEpisodeSummary;
}) {
  const body = useMemo(() => {
    const reviewdByGroupName = review?.data.reviewedByGroup?.name;
    const createdByGroupName = review?.data.createdByGroup?.name;

    if (review?.acknowledged)
      return (
        <Label>
          Safe Return request was acknowledged {review.reviewedBy ? `by ${review.reviewedBy.fullName} ` : ''}
          {reviewdByGroupName ? `at ${reviewdByGroupName} ` : ''}on {simpleDate(review.data.statusUpdatedAt)}
        </Label>
      );

    if (review?.active)
      return (
        <>
          <Label>Safe Return to</Label> <LabelBold>{previousSNFStay?.group.name}</LabelBold>{' '}
          <Label>
            requested by {review.createdBy?.fullName} {createdByGroupName ? 'at' : ''} {createdByGroupName} on{' '}
            {simpleDate(review.createdAt)}
          </Label>
        </>
      );

    if (isProviderUser)
      return (
        <>
          <Label>Click below to request a Safe Return to</Label> <LabelBold>{previousSNFStay?.group.name}</LabelBold>
        </>
      );

    return <Label>{previousSNFStay?.group.name}</Label>;
  }, [review, isProviderUser, previousSNFStay]);

  return <div>{body}</div>;
}

export type SafeReturnBlurbProps = {
  episode: Episode;
  locationEpisode: LocationEpisode;
  review?: SafeReturn;
  profile: Profile;
  previousSNFStay?: LocationEpisodeSummary;
};

export default function SafeReturnBlurb(props: SafeReturnBlurbProps) {
  const { review, locationEpisode, episode, profile, previousSNFStay } = props;

  const isStayArchived = useMemo(() => {
    return locationEpisode.archived || episode.archived;
  }, [locationEpisode.archived, episode.archived]);

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);

  const isProviderUser = !profile.isManagerFor(episode) && profile.isProviderFor(locationEpisode.groupId);

  return (
    <>
      <PaddedBlurb data-cy='safeReturn' $activeOutline={!!review} $greenBackground={review?.acknowledged}>
        <SafeReturnHeader>
          <Title review={review} isProviderUser={isProviderUser} />
        </SafeReturnHeader>
        <SafeReturnBodyContainer>
          {review?.acknowledged && <CircleCheckIcon fill={theme.colors.actionGreen} width={14} height={14} />}
          <Body review={review} isProviderUser={isProviderUser} previousSNFStay={previousSNFStay} />
        </SafeReturnBodyContainer>
        {!review && (
          <AccessGuard
            accessor={PERMISSIONS.reviewSafeReturnCreate}
            locationEpisode={locationEpisode}
            episode={episode}>
            <SafeReturnButton
              variant='call-to-action'
              fullWidth
              onClick={() => setShowRequestModal(true)}
              disabled={isStayArchived}>
              Request Safe Return
            </SafeReturnButton>
          </AccessGuard>
        )}
        {!!review?.active && (
          <AccessGuard accessor={PERMISSIONS.reviewSafeReturnEdit} locationEpisode={previousSNFStay} episode={episode}>
            <SafeReturnButton
              variant='call-to-action'
              fullWidth
              onClick={() => setShowAcknowledgeModal(true)}
              disabled={isStayArchived}>
              Acknowledge Safe Return Request
            </SafeReturnButton>
          </AccessGuard>
        )}
      </PaddedBlurb>

      {showRequestModal && (
        <RequestSafeReturnModal
          onCancel={() => setShowRequestModal(false)}
          onSuccess={() => setShowRequestModal(false)}
          locationEpisode={locationEpisode}
          snfLocationEpisode={previousSNFStay!}
          patientName={`${episode?.patient.name}`}
        />
      )}
      {showAcknowledgeModal && (
        <AcknowledgeSafeReturnModal
          onCancel={() => setShowAcknowledgeModal(false)}
          onSuccess={() => setShowAcknowledgeModal(false)}
          locationEpisode={locationEpisode}
          patientName={`${episode?.patient.name}`}
          review={review!}
        />
      )}
    </>
  );
}

const PaddedBlurb = styled(Blurb)<{ $activeOutline?: boolean; $greenBackground?: boolean }>`
  padding: 24px;
  ${({ $activeOutline }) =>
    $activeOutline &&
    `
    border-color: ${theme.colors.actionGreen};
  `}
  ${({ $greenBackground }) =>
    $greenBackground &&
    `
    background-color: #F7FBF3;
  `}
`;

const SafeReturnHeader = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  line-height: 14px;
`;

const SafeReturnBodyContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
`;

const SafeReturnButton = styled(Button)`
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.19px;
  margin-top: 16px;
  height: 32px;
  border-radius: 3px;
`;
